// lib
import { useState, useRef, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { createPortal } from 'react-dom'
import { Transition } from 'react-transition-group'

import { useWordProcessor } from '@aytm/app/stats/page/question-views/components/word-cloud/hooks/useWordProcessor'
import { useComponentWidth } from '@aytm/app/stats/page/question-views/components/charts/lib/hooks'

import { calcCloudBox, DEFAULT_WIDTH } from '@aytm/app/stats/page/question-views/components/word-cloud/lib/box'

import { renderWCTableProps } from '@aytm/app/stats/page/question-views/components/word-cloud/lib/table-utils'
import { removeCloudCorners } from '@aytm/app/stats/page/question-views/components/word-cloud/utils/removeCloudCorners'

import { isComponentOn } from '@aytm/app/stats/page/question-views/components/lib/utils'
import { isNoneChartMode } from '@aytm/app/stats/page/question-views/components/lib/chart-modes'

// components
import Payment from '@aytm/app/stats/page/question-views/components/word-cloud/Payment'
import Controls from '@aytm/app/stats/page/question-views/components/word-cloud/Controls'
import Chart from '@aytm/app/stats/page/question-views/components/word-cloud/Chart'
import Table from '@aytm/app/stats/components/data-views/table/components/Table'

import WordCloudControls from '@aytm/app/stats/page/question-views/oe/components/controls/WordCloud'

import WordCloudPlaceholder from '@aytm/app/stats/page/question-views/components/charts/placeholders/WordCloud'

// styles
import style from '@aytm/app/stats/page/question-views/components/word-cloud/styles/WordCloud.module.scss'

const PortalComponent = ({ children, containerId }) => {
  const intervalRef = useRef(null)
  const [container, setContainer] = useState(null)

  useEffect(() => {
    let portalContainer = document.querySelector(containerId)

    if (!portalContainer) {
      intervalRef.current = setInterval(() => {
        portalContainer = document.querySelector(containerId)
        if (portalContainer) {
          clearInterval(intervalRef.current)
          setContainer(portalContainer)
        }
      }, 100)
    } else setContainer(portalContainer)

    return () => {
      clearInterval(intervalRef.current)
    }
  }, [containerId])

  return container ? createPortal(children, container) : null
}

PortalComponent.propTypes = {
  children: PropTypes.any,
  containerId: PropTypes.string
}

PortalComponent.displayName = 'PortalComponent'

const WordCloud = ({
  width,
  height,
  globalChartMode,
  questionChartMode = null,
  model,
  data,
  config,
  features,
  components,
  applyToAllLabel,
  isVerbatimType,
  isSurveyGuest,
  isLoading,
  disabled,
  onParamsChange,
  onMaskUploaded,
  onWordClick,
  isWordClickable,
  onReset,
  onSave,
  onApplyToAll,
  onExport
}) => {
  // Process words
  // words: PropTypes.shape({
  //   words: PropTypes.array.isRequired,
  //   sentences: PropTypes.array,
  //   spell: PropTypes.object,
  //   noun: PropTypes.object,
  //   index: PropTypes.object,
  //   caseIndex: PropTypes.object,
  //   spellIndex: PropTypes.object,
  //   nounIndex: PropTypes.object
  // }).isRequired,
  const { words } = useWordProcessor(data.rawData, config, model?.useRawData)

  // the cloud measures the column it is drawn in, the way every other chart does; a mounting view
  // that fixes its dimensions still wins
  const { width: measuredWidth } = useComponentWidth({ defaultWidth: DEFAULT_WIDTH })
  const box = calcCloudBox({ width, height, measuredWidth, orientation: config.orientation })

  const [collapsed, setCollapsed] = useState(false)

  const chartRef = useRef(null)

  const tableProps = useMemo(() => renderWCTableProps(words, config.mergeWords), [words, config.mergeWords])

  const onExportClick = (key, callback) => {
    const [fileType] = key.split('-', 1)

    let data

    if ('csv' === fileType) {
      const cell = (1 === config.mergeWords && 'times') || 'f'
      data = words.filter(v => v?.[cell] != null)
      data.sort((a, b) => b[cell] - a[cell])
    } else {
      data = (null !== chartRef.current ? removeCloudCorners(chartRef.current.cloneNode(true)) : void 0) || null
    }

    onExport({ fileType }, data, callback)
  }

  const onPresetChange = preset => {
    if (String(preset).startsWith('custom-')) {
      const index = parseInt(preset.replace('custom-', ''), 10)
      onParamsChange({ currentPresetId: index })
    } else onParamsChange({ currentPresetId: preset })
  }

  return (
    <>
      {features.word_clouds.available ? (
        <Payment features={features} />
      ) : features.word_clouds.disabled || disabled ? null : isComponentOn(components, 'controls') &&
        isComponentOn(components, 'chart') &&
        !isNoneChartMode(questionChartMode, globalChartMode) ? (
        <>
          <PortalComponent containerId={`#oe-controls-${model.id}`}>
            <WordCloudControls
              model={model}
              data={data}
              config={config}
              features={features}
              applyToAllLabel={applyToAllLabel}
              collapsed={collapsed}
              isVerbatimType={isVerbatimType}
              isSurveyGuest={isSurveyGuest}
              onPresetChange={onPresetChange}
              onApplyToAll={onApplyToAll}
              onExport={onExportClick}
              onToggle={() => setCollapsed(!collapsed)}
            />
          </PortalComponent>
          <Transition in={!collapsed} timeout={500}>
            {status => (
              <div className={`${style['transition-panel']}${status ? ' ' + style['transition-panel-' + status] : ''}`}>
                <Controls
                  model={model}
                  data={data}
                  words={words}
                  config={config}
                  disabled={disabled}
                  isSurveyGuest={isSurveyGuest}
                  onParamsChange={onParamsChange}
                  onMaskUploaded={onMaskUploaded}
                  onReset={onReset}
                  onSave={onSave}
                />
              </div>
            )}
          </Transition>
        </>
      ) : null}
      {isComponentOn(components, 'chart') && !isNoneChartMode(questionChartMode, globalChartMode) ? (
        <WordCloudPlaceholder height={isLoading ? box.height : null} isLoading={isLoading}>
          <Chart
            ref={chartRef}
            width={box.width}
            height={box.height}
            model={model}
            words={words}
            config={config}
            scope={data.scope}
            onParamsChange={onParamsChange}
            onWordClick={onWordClick}
            isWordClickable={isWordClickable}
          />
        </WordCloudPlaceholder>
      ) : null}
      {isComponentOn(components, 'table') ? <Table cols={tableProps.cols} rows={tableProps.rows} /> : null}
    </>
  )
}

WordCloud.propTypes = {
  width: PropTypes.number,
  height: PropTypes.number,
  globalChartMode: PropTypes.string,
  questionChartMode: PropTypes.string,
  model: PropTypes.shape({
    id: PropTypes.string,
    useRawData: PropTypes.bool,
    changed: PropTypes.bool
  }),
  data: PropTypes.object,
  config: PropTypes.shape({
    currentPresetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    activeWord: PropTypes.string,
    fontFamily: PropTypes.string,
    colorStyle: PropTypes.oneOf(['adj_levels', 'levels', 'gradient', 'circle']),
    colors: PropTypes.arrayOf(PropTypes.string),
    background: PropTypes.string,
    mask: PropTypes.string,
    customMaskId: PropTypes.string,
    customMaskPath: PropTypes.string,
    angles: PropTypes.arrayOf(PropTypes.number),
    maxWords: PropTypes.number,
    scaleFunction: PropTypes.string,
    rangeStart: PropTypes.number,
    rangeEnd: PropTypes.number,
    spell: PropTypes.oneOf([0, 1]),
    caps: PropTypes.oneOf([0, 1]),
    mergeWords: PropTypes.oneOf([0, 1]),
    twoWords: PropTypes.oneOf([0, 1]),
    junk: PropTypes.oneOf([0, 1]),
    stop1: PropTypes.oneOf([0, 1]),
    stop2: PropTypes.oneOf([0, 1]),
    stop3: PropTypes.oneOf([0, 1]),
    stop4: PropTypes.oneOf([0, 1]),
    stop5: PropTypes.oneOf([0, 1]),
    blackList: PropTypes.arrayOf(PropTypes.string),
    randIndex: PropTypes.number,
    orientation: PropTypes.oneOf(['h', 'v'])
  }).isRequired,
  features: PropTypes.object,
  components: PropTypes.object,
  applyToAllLabel: PropTypes.string,
  isVerbatimType: PropTypes.bool,
  isSurveyGuest: PropTypes.bool,
  isLoading: PropTypes.bool,
  disabled: PropTypes.bool,
  onParamsChange: PropTypes.func,
  /**
   * Called after a custom mask operation completes.
   * - Upload success → receives the server response `{ id, original: { url }, ... }`.
   * - Delete       → receives `null` (user clicks × next to "custom mask").
   * Consumer should treat a nullish argument as "clear customMaskId / customMaskPath".
   */
  onMaskUploaded: PropTypes.func,
  onWordClick: PropTypes.func,
  isWordClickable: PropTypes.func,
  onReset: PropTypes.func,
  onSave: PropTypes.func,
  onApplyToAll: PropTypes.func,
  onExport: PropTypes.func
}

WordCloud.displayName = 'WordCloud'

export default WordCloud
